import React, { useState, useEffect, useCallback, useMemo } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import {
  getPendingApiAccessRequests,
  PendingApiAccessRequest,
} from "../../Admin-Api/GetPendinApiAccessRequests";
import { approveApiAccessRequest } from "../../Admin-Api/ApproveApiAccessRequest";
import { rejectApiAccessRequest } from "../../Admin-Api/RejectApiAccessRequest";
import { getAllApiUsers, ApiUser } from "../../Admin-Api/GetAllApiUsers";
import { enableApiUser } from "../../Admin-Api/EnableApiUser";
import { disableApiUser } from "../../Admin-Api/DisableApiUser";
import {
  getApiAccessRequestHistory,
  ApiAccessRequestHistory,
} from "../../Admin-Api/GetApiAccessRequestHistory";
import { useLocalization } from "../Context/MultiLanguageContext";
import { Search } from "lucide-react";

interface CustomJwtPayload {
  RoleId: string;
}

type MainTab = "requests" | "apiUsers";
type ApiUsersView = "users" | "history";

const formatDateTime = (dateString: string | null) => {
  if (!dateString) return "-";

  const date = new Date(dateString);

  return Number.isNaN(date.getTime())
    ? dateString
    : date.toLocaleDateString("en-GB");
};

const ManageApiUser: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLocalization();

  const [activeTab, setActiveTab] = useState<MainTab>("apiUsers");
  const [apiUsersView, setApiUsersView] = useState<ApiUsersView>("users");

  const [requests, setRequests] = useState<PendingApiAccessRequest[]>([]);
  const [apiUsers, setApiUsers] = useState<ApiUser[]>([]);
  const [history, setHistory] = useState<ApiAccessRequestHistory[]>([]);

  const [searchText, setSearchText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [processingUserId, setProcessingUserId] = useState<number | null>(null);
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);

  const filteredRequests = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!query) return requests;

    return requests.filter((request) =>
      String(request.userName || "").toLowerCase().includes(query) ||
      String(request.email || "").toLowerCase().includes(query) ||
      String(request.phoneNumber || "").toLowerCase().includes(query) ||
      String(request.status || "").toLowerCase().includes(query)
    );
  }, [requests, searchText]);

  const filteredApiUsers = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!query) return apiUsers;

    return apiUsers.filter((user) =>
      String(user.userName || "").toLowerCase().includes(query) ||
      String(user.email || "").toLowerCase().includes(query) ||
      String(user.phoneNumber || "").toLowerCase().includes(query)
    );
  }, [apiUsers, searchText]);

  const filteredHistory = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!query) return history;

    return history.filter((item) =>
      String(item.userName || "").toLowerCase().includes(query) ||
      String(item.email || "").toLowerCase().includes(query) ||
      String(item.phoneNumber || "").toLowerCase().includes(query) ||
      String(item.status || "").toLowerCase().includes(query) ||
      String(item.reviewerName || "").toLowerCase().includes(query)
    );
  }, [history, searchText]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("No auth token found. Please log in.");
      navigate("/");
      return;
    }

    try {
      const decodedToken = jwtDecode<CustomJwtPayload>(token);
      if (decodedToken.RoleId !== "1") {
        toast.error("Unauthorized access: RoleId is not 1");
        navigate("/");
      }
    } catch {
      toast.error("Invalid token. Please log in again.");
      navigate("/");
    }
  }, [navigate]);

  const fetchPendingRequests = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No auth token found");

      const data = await getPendingApiAccessRequests(token, t);
      setRequests(data);
    } catch {
      setRequests([]);
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  const fetchApiUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No auth token found");

      const data = await getAllApiUsers(token, t);
      setApiUsers(data);
    } catch {
      setApiUsers([]);
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  const fetchHistory = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No auth token found");

      const data = await getApiAccessRequestHistory(token, t);
      setHistory(data);
    } catch {
      setHistory([]);
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (activeTab === "requests") {
      fetchPendingRequests();
    } else if (apiUsersView === "users") {
      fetchApiUsers();
    } else {
      fetchHistory();
    }
  }, [activeTab, apiUsersView, fetchPendingRequests, fetchApiUsers, fetchHistory]);

  useEffect(() => {
    const handleClick = () => setOpenMenuId(null);

    if (openMenuId !== null) {
      window.addEventListener("click", handleClick);
    }

    return () => window.removeEventListener("click", handleClick);
  }, [openMenuId]);

  const handleTabChange = (tab: MainTab) => {
    setActiveTab(tab);
    setSearchText("");
    setApiUsersView("users");
  };

  const handleApiUsersViewChange = (view: ApiUsersView) => {
    setApiUsersView(view);
    setSearchText("");
  };

  const handleApprove = async (userId: number) => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("No auth token found. Please log in.");
      return;
    }

    setProcessingUserId(userId);
    try {
      const response = await approveApiAccessRequest(token, userId, t);
      toast.success(response.message);
      await fetchPendingRequests();
    } catch {
      // API helper handles error toast
    } finally {
      setProcessingUserId(null);
    }
  };

  const handleReject = async (userId: number) => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("No auth token found. Please log in.");
      return;
    }

    setProcessingUserId(userId);
    try {
      const response = await rejectApiAccessRequest(token, userId, t);
      toast.success(response.message);
      await fetchPendingRequests();
    } catch {
      // API helper handles error toast
    } finally {
      setProcessingUserId(null);
    }
  };

  const handleEnable = async (userId: number) => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("No auth token found. Please log in.");
      return;
    }

    setProcessingUserId(userId);
    try {
      const success = await enableApiUser(token, userId, t);
      if (success) {
        toast.success("API user enabled successfully.");
        await fetchApiUsers();
      } else {
        toast.error("Failed to enable API user.");
      }
    } catch {
      // API helper handles error toast
    } finally {
      setProcessingUserId(null);
    }
  };

  const handleDisable = async (userId: number) => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("No auth token found. Please log in.");
      return;
    }

    setProcessingUserId(userId);
    try {
      const success = await disableApiUser(token, userId, t);
      if (success) {
        toast.success("API user disabled successfully.");
        await fetchApiUsers();
      } else {
        toast.error("Failed to disable API user.");
      }
    } catch {
      // API helper handles error toast
    } finally {
      setProcessingUserId(null);
    }
  };

  const getRecordCount = () => {
    if (activeTab === "requests") {
      if (!requests.length) return null;
      return searchText.trim()
        ? `${filteredRequests.length} of ${requests.length} Records`
        : `${requests.length} Records`;
    }

    if (apiUsersView === "users") {
      if (!apiUsers.length) return null;
      return searchText.trim()
        ? `${filteredApiUsers.length} of ${apiUsers.length} Records`
        : `${apiUsers.length} Records`;
    }

    if (!history.length) return null;
    return searchText.trim()
      ? `${filteredHistory.length} of ${history.length} Records`
      : `${history.length} Records`;
  };

  const getSectionTitle = () => {
    if (activeTab === "requests") return "API Access Requests";
    if (apiUsersView === "users") return "API Users";
    return "Request History";
  };

  const renderLoading = () => (
    <div className="bg-white/80 backdrop-blur-sm rounded-xl p-8 shadow-sm text-center text-gray-500">
      Loading...
    </div>
  );

  const renderEmpty = (message: string) => (
    <div className="bg-white/80 backdrop-blur-sm rounded-xl p-8 shadow-sm text-center text-gray-500">
      {message}
    </div>
  );

  const renderRequestsTable = () => (
    <>
      <div className="hidden md:block overflow-x-auto rounded-md">
        <table className="min-w-full">
          <thead>
            <tr className="bg-blue-500  border-b border-gray-200">
              {["User Name", "Email", "Phone Number", "Status", "Requested At", "Actions"].map((header) => (
                <th key={header} className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredRequests.map((request) => {
              const isProcessing = processingUserId === request.userId;
              return (
                <tr key={request.id} className="hover:bg-blue-50/50 transition-colors duration-150">
                  <td className="px-4 py-4 text-left text-[14px] text-[#202124]">{request.userName || "-"}</td>
                  <td className="px-4 py-4 text-left text-[14px] text-[#202124] break-all">{request.email || "-"}</td>
                  <td className="px-4 py-4 text-left text-[14px] text-[#202124]">{request.phoneNumber || "-"}</td>
                  <td className="px-4 py-4 text-left text-[14px] text-[#202124]">{request.status || "-"}</td>
                  <td className="px-4 py-4 text-left text-[14px] text-[#202124]">{formatDateTime(request.requestedAt)}</td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => handleApprove(request.userId)}
                        disabled={isProcessing}
                        className="px-3 py-1.5 text-sm text-green-600 border border-green-600 rounded-md hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed 
                        sm:text-green-600 md:text-green-600 md:hover:bg-green-50"
                      >
                        Approve
                      </button>

                      <button
                        type="button"
                        onClick={() => handleReject(request.userId)}
                        disabled={isProcessing}
                        className="px-3 py-1.5 text-sm font-normal text-red-600 border border-red-600 rounded-md hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed
                        sm:text-red-600 md:text-red-600 md:hover:bg-red-50"
                      >
                        Reject
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="md:hidden space-y-4">
        {filteredRequests.map((request) => {
          const isProcessing = processingUserId === request.userId;
          return (
            <div key={request.id} className="bg-white/90 rounded-xl shadow-sm border border-gray-100 p-4">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <h5 className="text-base font-semibold text-gray-800">{request.userName || "-"}</h5>
                  <p className="text-sm text-gray-500 break-all">{request.email || "-"}</p>
                </div>
                <span className="text-sm text-gray-800 shrink-0">{request.status || "-"}</span>
              </div>
              <div className="space-y-2 text-sm text-gray-700">
                <div className="flex justify-between gap-3">
                  <span className="text-gray-500">Phone</span>
                  <span className="text-right">{request.phoneNumber || "-"}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-gray-500">Requested</span>
                  <span className="text-right">{formatDateTime(request.requestedAt)}</span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-4">
                <button
                  type="button"
                  onClick={() => handleApprove(request.userId)}
                  disabled={isProcessing}
                  className="px-4 py-2 !bg-white !text-green-600 !border !border-green-600 rounded-lg font-medium hover:!bg-green-50"
                >
                  Approve
                </button>
                <button
                  type="button"
                  onClick={() => handleReject(request.userId)}
                  disabled={isProcessing}
                  className="px-4 py-2 !bg-white !text-red-600 !border !border-red-600 rounded-lg font-medium hover:!bg-red-50"
                >
                  Reject
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );

  const renderApiUsersTable = () => (
    <>
      <div className="hidden md:block overflow-x-auto rounded-md">
        <table className="min-w-full">
          <thead>
            <tr className="bg-blue-500 border-b border-gray-200">
              {["User Name", "Email", "Phone Number", "Status", "Actions"].map((header) => (
                <th
                  key={header}
                  className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredApiUsers.map((user) => {
              const isProcessing = processingUserId === user.id;
              return (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className={`px-4 py-4 text-left text-[14px] ${user.userName ? "text-[#202124]" : "text-gray-400"
                    }`}>
                    {user.userName || "Not Provided"}
                  </td>

                  <td className={`px-4 py-4 text-left text-[14px] break-all ${user.email ? "text-[#202124]" : "text-gray-400"
                    }`}>
                    {user.email || "Not Provided"}
                  </td>

                  <td className={`px-4 py-4 text-left text-[14px] ${user.phoneNumber ? "text-[#202124]" : "text-gray-400"
                    }`}>
                    {user.phoneNumber || "Not Provided"}
                  </td>

                  <td className="px-4 py-4 text-left text-[14px] text-[#202124]">
                    {user.isApiUser ? "API User" : "Normal User"}
                  </td>
                  <td className="px-4 py-4 text-left">
                    {user.isApiUser ? (
                      <button
                        type="button"
                        onClick={() => handleDisable(user.id)}
                        disabled={processingUserId === user.id}
                        className="px-3 py-1.5 text-sm font-normal text-red-600 border border-red-600 rounded-md hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed
                        sm:text-red-600 md:text-red-600 md:hover:bg-red-50"
                      >
                        Disable
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleEnable(user.id)}
                        disabled={processingUserId === user.id}
                        className="px-3 py-1.5 text-sm text-green-600 border border-green-600 rounded-md hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed 
                        sm:text-green-600 md:text-green-600 md:hover:bg-green-50"
                      >
                        Enable
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="md:hidden space-y-4">
        {filteredApiUsers.map((user) => {
          const isProcessing = processingUserId === user.id;
          return (
            <div key={user.id} className="bg-white/90 rounded-xl shadow-sm border border-gray-100 p-4">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <h5 className="text-base font-semibold text-gray-800">{user.userName || "-"}</h5>
                  <p className="text-sm text-gray-500 break-all">{user.email || "-"}</p>
                </div>
                <span className="text-sm text-gray-800 shrink-0">
                  API User: {user.isApiUser ? "Yes" : "No"}
                </span>
              </div>
              <div className="text-sm text-gray-700 mb-4">
                <div className="flex justify-between gap-3">
                  <span className="text-gray-500">Phone</span>
                  <span className="text-right">{user.phoneNumber || "-"}</span>
                </div>
              </div>
              {user.isApiUser ? (
                <button
                  type="button"
                  onClick={() => handleDisable(user.id)}
                  disabled={isProcessing}
                  className="w-full h-10 text-sm !bg-white !text-red-600 !border !border-red-600 
                  rounded-lg font-medium hover:!bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Disable
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => handleEnable(user.id)}
                  disabled={isProcessing}
                  className="w-full h-10 text-sm !bg-white !text-green-600 !border !border-green-600 
                  rounded-lg font-medium hover:!bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Enable
                </button>
              )}
            </div>
          );
        })}
      </div>
    </>
  );

  const renderHistoryTable = () => (
    <>
      <div className="hidden md:block overflow-x-auto rounded-md">
        <table className="min-w-full">
          <thead>
            <tr className="bg-blue-500 border-b border-gray-200">
              {["User Name", "Email", "Phone Number", "Status", "Requested At", "Reviewed At", "Reviewed By"].map((header) => (
                <th
                  key={header}
                  className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredHistory
              .filter((item) => item.status?.toLowerCase() !== "pending")
              .map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4 text-left text-[14px] text-[#202124]">{item.userName || "-"}</td>
                  <td className="px-4 py-4 text-left text-[14px] text-[#202124] break-all">{item.email || "-"}</td>
                  <td className="px-4 py-4 text-left text-[14px] text-[#202124]">{item.phoneNumber || "-"}</td>
                  <td className="px-4 py-4 text-left text-[14px] text-[#202124]">{item.status || "-"}</td>
                  <td className="px-4 py-4 text-left text-[14px] text-[#202124]">{formatDateTime(item.requestedAt)}</td>
                  <td className="px-4 py-4 text-left text-[14px] text-[#202124]">{formatDateTime(item.reviewedAt)}</td>
                  <td className="px-4 py-4 text-left text-[14px] text-[#202124]">{item.reviewerName || "-"}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <div className="md:hidden space-y-4">
        {filteredHistory.map((item) => (
          <div key={item.id} className="bg-white/90 rounded-xl shadow-sm border border-gray-100 p-4">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <h5 className="text-base font-semibold text-gray-800">{item.userName || "-"}</h5>
                <p className="text-sm text-gray-500 break-all">{item.email || "-"}</p>
              </div>
              <span className="text-sm text-gray-800 shrink-0">{item.status || "-"}</span>
            </div>
            <div className="space-y-2 text-sm text-gray-700">
              <div className="flex justify-between gap-3">
                <span className="text-gray-500">Phone</span>
                <span className="text-right">{item.phoneNumber || "-"}</span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-gray-500">Requested</span>
                <span className="text-right">{formatDateTime(item.requestedAt)}</span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-gray-500">Reviewed</span>
                <span className="text-right">{formatDateTime(item.reviewedAt)}</span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-gray-500">Reviewed By</span>
                <span className="text-right">{item.reviewerName || "-"}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );

  const renderTabContent = () => {
    if (isLoading) return renderLoading();

    if (activeTab === "requests") {
      if (requests.length === 0) return renderEmpty("No pending API access requests.");
      if (filteredRequests.length === 0) return renderEmpty("No matching API access requests.");
      return renderRequestsTable();
    }

    if (apiUsersView === "users") {
      if (apiUsers.length === 0) return renderEmpty("No API users found.");
      if (filteredApiUsers.length === 0) return renderEmpty("No matching API users.");
      return renderApiUsersTable();
    }

    if (history.length === 0) return renderEmpty("No API access request history found.");
    if (filteredHistory.length === 0) return renderEmpty("No matching history records.");
    return renderHistoryTable();
  };

  const recordCount = getRecordCount();

  return (
    <div className="h-screen overflow-auto bg-[#f5f7fb] select-none">
      <div className="flex flex-col min-h-screen">
        <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 px-4 sm:px-8 py-4 bg-white shadow-sm">
          <h1 className="text-2xl font-bold text-gray-800 flex items-center">
            <span className="bg-blue-600 w-2 h-8 rounded mr-3"></span>
            Manage API User
          </h1>

          <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-lg w-fit">

            <button
              type="button"
              onClick={() => handleTabChange("apiUsers")}
              className={`px-4 py-2 rounded-md text-sm font-medium transition ${activeTab === "apiUsers"
                ? "bg-white shadow text-blue-600"
                : "text-gray-600 hover:text-gray-800"
                }`}
            >
              API Users
            </button>

            <button
              type="button"
              onClick={() => handleTabChange("requests")}
              className={`px-4 py-2 rounded-md text-sm font-medium transition ${activeTab === "requests"
                ? "bg-white shadow text-blue-600"
                : "text-gray-600 hover:text-gray-800"
                }`}
            >
              API Key Requests
            </button>
          </div>
        </header>

        <main className="flex-1 overflow-auto px-6 py-5">
          <div className="max-w-7xl mx-auto">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 px-5 py-4 border-b border-gray-200">
                <div className="flex items-center">
                  {activeTab === "apiUsers" ? (
                    <div className="flex items-center gap-8">
                      <button
                        type="button"
                        onClick={() => handleApiUsersViewChange("users")}
                        className={`h-[36px] flex items-center text-sm font-medium border-b-2 ${apiUsersView === "users"
                          ? "text-blue-600 border-blue-600"
                          : "text-gray-500 border-transparent"
                          }`}
                      >
                        Users List
                      </button>

                      <button
                        type="button"
                        onClick={() => handleApiUsersViewChange("history")}
                        className={`h-[36px] flex items-center text-sm font-medium border-b-2 ${apiUsersView === "history"
                          ? "text-blue-600 border-blue-600"
                          : "text-gray-500 border-transparent"
                          }`}
                      >
                        Request History
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <h4 className="text-[18px] font-semibold text-[#202124]">
                        API Access Requests
                      </h4>

                      {recordCount && (
                        <span className="ml-5 text-[14px] text-[#70757A]">
                          {recordCount}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                <div className="relative w-full sm:w-[300px]">
                  <Search
                    size={17}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-500"
                  />
                  <input
                    type="text"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    placeholder="Search by name, email or phone"
                    className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-800 focus:outline-none focus:ring-1 focus:ring-blue-400"
                  />
                </div>
              </div>

              <div className="px-5 py-4">

                {renderTabContent()}
              </div>
            </div>
          </div>
        </main>
      </div>

      <ToastContainer />
    </div>
  );
};

export default ManageApiUser;
